// utils/imageUtils.js
export class ImageManager {
  constructor() {
    // Récupération de la limite depuis les variables d'environnement
    this.maxSizeBytes = (import.meta.env.VITE_MAX_IMAGE_SIZE_MB || 0.4) * 1024 * 1024;
  }

  /**
   * Convertit un fichier image en Base64
   * @param {File} file - Le fichier image à convertir
   * @returns {Promise<{base64: string, metadata: object}>}
   */
  async fileToBase64(file) {
    return new Promise((resolve, reject) => {
      // Vérification de la taille
      if (file.size > this.maxSizeBytes) {
        reject(new Error(`Image trop volumineuse. Taille max: ${this.maxSizeBytes / (1024 * 1024)}MB`));
        return;
      }

      // Vérification du type MIME
      if (!file.type.startsWith('image/')) {
        reject(new Error('Le fichier doit être une image'));
        return;
      }

      const reader = new FileReader();
      
      reader.onload = () => {
        const base64 = reader.result; // Contient déjà le préfixe data:image/...;base64,
        
        // Métadonnées utiles
        const metadata = {
          name: file.name,
          size: file.size,
          type: file.type,
          lastModified: file.lastModified,
          sizeFormatted: this.formatFileSize(file.size)
        };

        resolve({ base64, metadata });
      };

      reader.onerror = () => {
        reject(new Error('Erreur lors de la lecture du fichier'));
      };

      reader.readAsDataURL(file);
    });
  }

  /**
   * Redimensionne une image si nécessaire pour respecter la limite de taille
   * @param {File} file - Le fichier original
   * @param {number} maxWidth - Largeur max (optionnel)
   * @param {number} maxHeight - Hauteur max (optionnel)
   * @param {number} quality - Qualité JPEG (0.1 à 1.0)
   * @returns {Promise<{base64: string, metadata: object}>}
   */
  async resizeAndConvert(file, maxWidth = 1200, maxHeight = 800, quality = 0.8) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      img.onload = () => {
        // Calcul des nouvelles dimensions en gardant le ratio
        let { width, height } = this.calculateDimensions(
          img.width, 
          img.height, 
          maxWidth, 
          maxHeight
        );

        canvas.width = width;
        canvas.height = height;

        // Dessin de l'image redimensionnée
        ctx.drawImage(img, 0, 0, width, height);

        // Conversion en Base64 avec compression
        const base64 = canvas.toDataURL('image/jpeg', quality);

        // Vérification de la taille après compression
        const sizeInBytes = this.base64ToBytes(base64);
        
        if (sizeInBytes > this.maxSizeBytes) {
          // Si encore trop gros, on réduit la qualité
          const newQuality = Math.max(0.1, quality * 0.8);
          if (newQuality !== quality) {
            this.resizeAndConvert(file, maxWidth, maxHeight, newQuality)
              .then(resolve)
              .catch(reject);
            return;
          } else {
            reject(new Error('Impossible de compresser suffisamment l\'image'));
            return;
          }
        }

        const metadata = {
          name: file.name,
          originalSize: file.size,
          compressedSize: sizeInBytes,
          type: 'image/jpeg',
          dimensions: { width, height },
          originalDimensions: { width: img.width, height: img.height },
          quality: quality,
          sizeFormatted: this.formatFileSize(sizeInBytes)
        };

        resolve({ base64, metadata });
      };

      img.onerror = () => {
        reject(new Error('Erreur lors du chargement de l\'image'));
      };

      // Création de l'URL de l'image pour le chargement
      img.src = URL.createObjectURL(file);
    });
  }

  /**
   * Calcule les nouvelles dimensions en gardant le ratio
   */
  calculateDimensions(originalWidth, originalHeight, maxWidth, maxHeight) {
    let width = originalWidth;
    let height = originalHeight;

    // Redimensionner si nécessaire
    if (width > maxWidth) {
      height = (height * maxWidth) / width;
      width = maxWidth;
    }

    if (height > maxHeight) {
      width = (width * maxHeight) / height;
      height = maxHeight;
    }

    return { width: Math.round(width), height: Math.round(height) };
  }

  /**
   * Calcule la taille en bytes d'une string Base64
   */
  base64ToBytes(base64String) {
    // Supprime le préfixe data:image/...;base64,
    const base64Data = base64String.split(',')[1] || base64String;
    // Chaque caractère Base64 représente 6 bits, donc taille = (length * 6) / 8
    // Approximation: taille ≈ length * 0.75
    return Math.round(base64Data.length * 0.75);
  }

  /**
   * Formate la taille du fichier pour l'affichage
   */
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Valide qu'une string est un Base64 valide
   */
  isValidBase64Image(base64String) {
    try {
      // Vérification du format data:image/...;base64,
      const dataUrlPattern = /^data:image\/(png|jpeg|jpg|gif|webp);base64,/;
      return dataUrlPattern.test(base64String);
    } catch (error) {
      return false;
    }
  }
}